/**
 * App-theme model (ТЗ §7) — pure logic for the storefront's selectable themes.
 *
 * Two concrete looks are shipped: ``melusine`` (the signature night-sky default, expressed
 * by the ``:root`` token set) and ``dark`` (a minimalist flat-dark variant under
 * ``:root[data-theme="dark"]``). ``system`` follows the OS ``prefers-color-scheme``.
 *
 * The choice is persisted in ``localStorage`` and applied by toggling ``data-theme`` on the
 * document root, so it survives reloads and is set *before* first paint (see ``initTheme``).
 *
 * NOTE (backend): the user profile has no theme field yet, so cross-device sync is not
 * possible. When ``MeSerializer`` gains a ``theme`` field this module should hydrate from /
 * persist to the profile in addition to ``localStorage``.
 */

/** The user-selectable theme options. */
export type Theme = "melusine" | "dark" | "oled" | "system";

/** The concrete look actually rendered (``system`` resolves to one of these). */
export type ResolvedTheme = "melusine" | "dark" | "oled";

/** Ordered options for the settings UI, with Russian labels + descriptions. */
export const THEME_OPTIONS: readonly {
  readonly value: Theme;
  readonly label: string;
  readonly description: string;
}[] = [
  {
    value: "melusine",
    label: "Melusine",
    description: "Тёмно-синяя фэнтези-тема с золотом",
  },
  {
    value: "dark",
    label: "Тёмная",
    description: "Минималистичная, приглушённая",
  },
  {
    value: "oled",
    label: "OLED",
    description: "Чёрный фон для OLED-экранов, золото-акцент",
  },
  {
    value: "system",
    label: "Системная",
    description: "Как в настройках устройства",
  },
] as const;

export const THEME_STORAGE_KEY = "ms-theme";
export const DEFAULT_THEME: Theme = "melusine";

/** Type guard for a persisted/raw theme value. */
export function isTheme(value: unknown): value is Theme {
  return (
    value === "melusine" ||
    value === "dark" ||
    value === "oled" ||
    value === "system"
  );
}

/** ``true`` when the OS currently prefers a dark colour scheme. */
export function prefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

/** Resolve a (possibly ``system``) theme to the concrete look to render. */
export function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === "system") {
    return prefersDark() ? "dark" : "melusine";
  }
  return theme;
}

/** Read the persisted theme (falls back to the default on garbage / no storage). */
export function loadTheme(): Theme {
  if (typeof localStorage === "undefined") {
    return DEFAULT_THEME;
  }
  const raw = localStorage.getItem(THEME_STORAGE_KEY);
  return isTheme(raw) ? raw : DEFAULT_THEME;
}

/** Persist the chosen theme to ``localStorage`` (no-op without storage). */
export function saveTheme(theme: Theme): void {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }
}

/**
 * Apply a theme by toggling ``data-theme`` on the document root. ``melusine`` is the
 * default ``:root`` token set, so its attribute is removed rather than set.
 */
export function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") {
    return;
  }
  const resolved = resolveTheme(theme);
  const root = document.documentElement;
  if (resolved === "melusine") {
    delete root.dataset["theme"];
  } else {
    root.dataset["theme"] = resolved;
  }
}

/** Apply the persisted theme at startup (call once, before React renders). */
export function initTheme(): void {
  applyTheme(loadTheme());
}
